import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { subjectDetailsApiService } from "modules/Clinical/SubjectDetails/api/subjectdetails.api";
import {
    SubjectDetailsResponse,
    AddDeviationReasonPayload,
} from "modules/Clinical/TimePoints/helpers/timpoints.types";

export const useSubjectDetailsService = ({
    studyid,
    subjectno,
    volunteerid,
}: {
    studyid?: number | null;
    subjectno?: string | null;
    volunteerid?: number | null;
} = {}) => {
    const queryClient = useQueryClient();

    // SelectAll
    const SelectAllQuery = useQuery<SubjectDetailsResponse>({
        queryKey: [
            "subject-details-selectAll",
            studyid,
            subjectno
        ],
        queryFn: () =>
            subjectDetailsApiService.getSelectAllData(
                studyid,
                subjectno
            ),
        enabled: !!studyid && !!subjectno,
    });


    const SelectQuery = useQuery<SubjectDetailsResponse>({
        queryKey: ["subject-details-select", studyid, volunteerid],
        queryFn: () => subjectDetailsApiService.getSelectData(studyid, volunteerid),
        enabled: !!studyid && !!volunteerid,
    });

    const UpdateActualTimesMutation = useMutation({
        mutationFn: (payload: FormData) =>
            subjectDetailsApiService.updateActualCollectionTimes(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["subject-details-selectAll", studyid, subjectno]
            });
            queryClient.invalidateQueries({
                queryKey: ["verification-notification-count"]
            });
        }
    });

    const CorrectSingleTimeMutation = useMutation({
        mutationFn: (payload: {
            samplecollectionid: number;
            samplecollectiontime: string;
            updatedby: number;
            updatereason: string;
        }) => subjectDetailsApiService.correctSingleCollectionTime(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["subject-details-selectAll", studyid, subjectno]
            });
            queryClient.invalidateQueries({
                queryKey: ["verification-notification-count"]
            });
        }
    });

    const AddDeviationReasonMutation = useMutation({
        mutationFn: (payload: AddDeviationReasonPayload) =>
            subjectDetailsApiService.addDeviationReason(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["subject-details-selectAll", studyid, subjectno]
            });
        }
    });

    const ScanSheetMutation = useMutation({
        mutationFn: (payload: FormData) =>
            subjectDetailsApiService.scanSheet(payload)
    });

    const VerifyGlobalMutation = useMutation({
        mutationFn: (payload: {
            studyid: number;
            subjectno: string;
            periodid: number;
            verifiedby: number;
            verifypin: string;
        }) => subjectDetailsApiService.verifySampleCollectionTimeGlobal(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["subject-details-selectAll", studyid, subjectno]
            });
            queryClient.invalidateQueries({
                queryKey: ["verification-notification-count"]
            });
        }
    });

    return {
        SelectAllQuery,
        SelectQuery,
        UpdateActualTimesMutation,
        CorrectSingleTimeMutation,
        AddDeviationReasonMutation,
        ScanSheetMutation,
        VerifyGlobalMutation,
    };
};

